import { useEffect, useState } from 'react';
import { User, RotateCcw, Trash2, Eye, MapPinned } from 'lucide-react';
import ConfirmModal from '../../../../components/ConfirmModal';
import { cekTipeDataJson } from '../../../../controller';


export interface Segel {
    id      : string,
    nsb     : string,
    nama    : string,
    alamat  : string,
    jumlah  : string,
}

export interface SegelHistory {
    m_lalu      : string,
    foto_meter  : any,
    foto_rumah  : string,
    koordinat   : string,
    modified_at : string
}

export default function DashboardSegel() {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); // bisa default 
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusLength, setStatusLength] = useState('10');
    const [showFormModal, setShowFormModal] = useState(false);
    const [statusFormModal, setStatusFormModal] = useState("none")
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [searchName, setSearchName] = useState<string>("");
    const [segelData, setSegelData] = useState<Segel[]>([]);
    const [selectedSegel, setSelectedSegel] = useState<Segel | null>(null);

    type SortKey = 'nsb' | 'nama' | 'alamat' | 'jumlah';

    const [sortKey, setSortKey] = useState<SortKey>('nsb');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }

        setCurrentPage(1);
    };

    const today = new Date();
    const todayYear = String(today.getFullYear());
    const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
    const todayDay = String(today.getDate()).padStart(2, '0');

    const [filterYear, setFilterYear] = useState<string>(todayYear);
    const [filterMonth, setFilterMonth] = useState<string>(todayMonth);
    const [filterDay, setFilterDay] = useState<string>(todayDay);

    const get = (k: any) => {
        if (!k) return '';
        if (typeof k === 'string') return k;
        return k.nama || k.username || '';
    }

    const filteredSegels = segelData.filter((segel) => { const keyword = searchName.trim().toLowerCase();
            if (!keyword) return true;
            return (segel.nama?.toLowerCase().includes(keyword) || segel.nsb?.toLowerCase().includes(keyword) );
        }).sort((first, second) => { 
            const firstValue = String(first[sortKey] ?? '').toLowerCase();
            const secondValue = String(second[sortKey] ?? '').toLowerCase();
            const result = firstValue.localeCompare(secondValue, 'id', { numeric: true, sensitivity: 'base', });
            return sortDirection === 'asc' ? result : -result;
        });

    const totalPages = Math.max(1,Math.ceil(filteredSegels.length / itemsPerPage));
    const paginatedSegels = filteredSegels.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);
    const renderSortIcon = (key: SortKey) => {
        if (sortKey !== key) return '↕';

        return sortDirection === 'asc' ? '↑' : '↓';
    }
    
    const fetchGetSegelAll = async () => {
      let key = process.env.REACT_APP_API_KEY || '';
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", key);
    
      const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow" as RequestRedirect
      };
    
      const apiUrl = (process.env.REACT_APP_API_URL_V2 + "/hublang/segel")
      const result = await fetch(apiUrl, requestOptions)
          .then((response) => response.json())
          .then((result) => result)
          .catch((error : any) => { return { "status" : 500, "message" : "Terjadi kesalahan pada server", "error" : error.message , data: []} });
        return result;
    }

    const fetchGetSegelHistory = async (nsb : string) => {
        try {
            let key = process.env.REACT_APP_API_KEY || '';
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Authorization", key);
            
            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow" as RequestRedirect
            };
            
            const apiUrl = (process.env.REACT_APP_API_URL_V2 + "/hublang/segel/history/" + nsb);
            const result = await fetch(apiUrl, requestOptions)
                .then((response) => response.json())
                .then((result) => result)
                .catch((error : any) => { return { "status" : 500, "message" : "Terjadi kesalahan pada server", "error" : error.message , data: []} });
                return result;
        } catch (error : any) {
            console.error(error);
            return { status : 500, message : "Error Masuk dari fetchGetSegelsHistory()" , error : error }
        }
    }

    const fetchDeleteSegelRecord = async (id : string) => {
        let key = process.env.REACT_APP_API_KEY || '';
        const myHeaders = new Headers();
        myHeaders.append("Authorization", key);

        const requestOptions: RequestInit = {
            method: "DELETE",
            headers: myHeaders,
            redirect: "follow" as RequestRedirect
        };

        const apiUrl = (process.env.REACT_APP_API_URL + "/hublang/segel/" + id);
        return await fetch(apiUrl, requestOptions)
            .then((response) => response.json())
            .then((result) => { return { "status" : result.status, "message" : result.message }; })
            .catch((error) => { return { "status" : 500, "message" : "Terjadi kesalahan pada server", "error" : error.message } });
    }

    const initDataSegel = async () => { 
        setLoading(true);
        try {
            const res = await fetchGetSegelAll();
            if (res.status === 200) {
                setSegelData(res.data); // pastikan API kirim data array karyawan
            } else {
                setSegelData([]);
            }
        } catch (error) {
            console.error(error);
            setSegelData([]);
        } finally {
            setLoading(false);
        }
    }

    const handleDetailSegel = (segel: Segel) => {
        setSelectedSegel(segel);
        setShowDetailModal(true);
    }

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<Segel | null>(null);

    const handleRequestDelete = (absen: Segel) => {
        setDeleteCandidate(absen);
        setShowDeleteConfirm(true);
    }

    const confirmDelete = async () => {
        if(!deleteCandidate) return;
        // await fetchDeleteAbsensiRecord(deleteCandidate.id);
        setShowDeleteConfirm(false);
        setDeleteCandidate(null);
        initDataSegel();
    }

    useEffect(() => {
        initDataSegel();
    }, []);

    const RenderTableSegel = ({ data, onOpenDetail, onRequestDelete, onRemoveState }: { 
        data: Segel[] | null; 
        onOpenDetail : (segel: Segel) => void;
        onRequestDelete?: (absen: Segel) => void;
        onRemoveState : () => void;
    }) => {
        return (
            <>
                {(data || []).map((segel) => (
                    <tr key={segel.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {segel.nsb}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {segel.nama}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {segel.alamat}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {segel.jumlah}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                                <button onClick={() => onOpenDetail(segel)} className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 p-1.5 rounded-md transition-colors" title="detail">
                                    <Eye className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={ async () => { try{ if (onRequestDelete) { onRequestDelete(segel); } else { if(window.confirm(`Yakin hapus ${segel.nsb} - ${segel.nama} ?`)) { 
                                        // await fetchDeleteAbsensiRecord(segel?.id); 
                                    } onRemoveState(); } } catch (err) { console.error("Gagal hapus:", err); } } }
                                    className="text-red-600 hover:text-red-900 hover:bg-red-50 p-1.5 rounded-md transition-colors" title="Hapus">
                                        <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </>
        );
    }

    const DetailModalSegel = () => {
        const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
        const [dataHistory, setDataHistory] = useState<SegelHistory[]>([]);
        const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
        const [selectedPhoto, setSelectedPhoto] = useState<{image:string; title: string; datetime?: string; type: 'meter_sebelum' | 'meter_sesudah' | 'foto_rumah'} | null>(null);
        const [photoGallery, setPhotoGallery] = useState<{image:string; title: string; datetime?: string; type: 'meter_sebelum' | 'meter_sesudah' | 'foto_rumah'}[]>([]);

        const openPhotoPreview = ( photos: {image:string; title: string; datetime?: string; type: 'meter_sebelum' | 'meter_sesudah' | 'foto_rumah'}[], index = 0) => {
            if (!photos.length) return;

            setPhotoGallery(photos);
            setSelectedPhotoIndex(index);
            setSelectedPhoto(photos[index]);
        };

        const navigatePhoto = (direction: number) => {
            if (!photoGallery.length) return;

            const nextIndex =
                (selectedPhotoIndex + direction + photoGallery.length) %
                photoGallery.length;

            setSelectedPhotoIndex(nextIndex);
            setSelectedPhoto(photoGallery[nextIndex]);
        };

        const initDataSegelHistory = async () => {
            setLoadingDetail(true);
            try {
                if (!selectedSegel?.nsb) {
                    setDataHistory([]);
                    return;
                }

                const res = await fetchGetSegelHistory(selectedSegel.nsb);
                const history = Array.isArray(res) ? res : res?.data;
                const historyRapi = history.map((el : any) => { return {...el, foto_meter : cekTipeDataJson(el.foto_meter) } })
                console.log(historyRapi);
                setDataHistory(Array.isArray(history) ? historyRapi : []);
            } catch (error) {
                setDataHistory([]);
            } finally {
                setLoadingDetail(false);
            }
        };

        const previewPhoto = (history: SegelHistory) => {
            const photos = [
                history.foto_meter?.meter_sebelum?.image && {
                    image: history.foto_meter.meter_sebelum.image,
                    title: 'Meter Sebelum',
                    datetime: history.foto_meter.meter_sebelum.datetime,
                },
                history.foto_meter?.meter_sesudah?.image && {
                    image: history.foto_meter.meter_sesudah.image,
                    title: 'Meter Sesudah',
                    datetime: history.foto_meter.meter_sesudah.datetime,
                }
            ].filter(Boolean) as {
                image: string;
                title: string;
                datetime?: string;
                type: 'meter_sebelum' | 'meter_sesudah' | 'foto_rumah';
            }[];

            openPhotoPreview(photos);
        };

        useEffect(() => {
            if (showDetailModal) {
                void initDataSegelHistory();
            }
        }, [showDetailModal, selectedSegel?.id]);

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/40" onClick={() => setShowDetailModal(false)} />
                <div className="relative w-11/12 max-w-lg rounded-2xl bg-white p-6 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Detail Segel Pelanggan - {selectedSegel?.nsb}</h3>
                    <button onClick={() => setShowDetailModal(false)} className="text-gray-500">✕</button>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm text-gray-600">Nomor Sambung : {selectedSegel?.nsb}</label>
                            <label className="block text-sm text-gray-600">Nama : {selectedSegel?.nama}</label>
                            <label className="block text-sm text-gray-600">Alamat : {selectedSegel?.alamat}</label>
                        </div>
                        <div>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Foto Rumah
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Meter Lalu - Waktu
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Aksi
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {
                                        loadingDetail?
                                        <>
                                            <tr>
                                                <td colSpan={6} className="text-center py-4 text-gray-500">
                                                    Sedang Loading . . .
                                                </td>
                                            </tr>
                                        </>
                                        :
                                        <>
                                        {(dataHistory || []).map((history, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                    {history.foto_rumah ? (
                                                        <img src={history.foto_rumah} alt={selectedSegel?.nama + " - " + selectedSegel?.nsb} onClick={() => openPhotoPreview([ { image: history.foto_rumah, title: 'Foto Rumah', type: 'foto_rumah', }, ]) } className="h-16 w-20 object-cover rounded-md border border-gray-200 shadow-sm" onError={(e) => { const target = e.currentTarget as HTMLImageElement; target.style.display = 'none'; }} />
                                                    ) : (
                                                        <span className="text-xs text-gray-400">Tidak ada foto</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {history.m_lalu} - {new Date(history.modified_at).toLocaleString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })} <br />
                                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${history.foto_meter.meter_sesudah ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"}`}>
                                                        {history.foto_meter.meter_sesudah ? "Sudah dieksekusi" : "Belum dieksekusi"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    <div className="flex space-x-2">
                                                        <button onClick={() => previewPhoto(history)} disabled={
                                                            !history.foto_meter?.meter_sebelum?.image &&
                                                            !history.foto_meter?.meter_sesudah?.image &&
                                                            !history.foto_rumah
                                                        }  className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 p-1.5 rounded-md transition-colors" title="detail">
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(history.koordinat)}`, '_blank', 'noopener,noreferrer')}
                                                            className="text-green-600 hover:text-green-900 hover:bg-green-50 p-1.5 rounded-md transition-colors" title="Buka di Google Maps" disabled={!history.koordinat}
                                                        >
                                                            <MapPinned className="w-4 h-4" />
                                                        </button>
                                                        
                                                    </div>
                                                </td>
                                            </tr>
                                         ))}
                                        </>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {selectedPhoto && (
                    <div
                        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
                        onClick={() => setSelectedPhoto(null)}
                    >
                        <div
                            className="relative flex w-full max-w-5xl flex-col items-center"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={() => setSelectedPhoto(null)}
                                className="absolute right-0 top-[-45px] rounded-full bg-white px-3 py-1 text-xl text-gray-700 shadow"
                                aria-label="Tutup preview"
                            >
                                ✕
                            </button>

                            {photoGallery.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => navigatePhoto(-1)}
                                        className="absolute left-0 top-1/2 z-10 -translate-x-3 -translate-y-1/2 rounded-full bg-white px-4 py-2 text-2xl text-gray-700 shadow hover:bg-gray-100"
                                        aria-label="Foto sebelumnya"
                                    >
                                        ‹
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigatePhoto(1)}
                                        className="absolute right-0 top-1/2 z-10 translate-x-3 -translate-y-1/2 rounded-full bg-white px-4 py-2 text-2xl text-gray-700 shadow hover:bg-gray-100"
                                        aria-label="Foto berikutnya"
                                    >
                                        ›
                                    </button>
                                </>
                            )}
                            <img
                                src={selectedPhoto.image}
                                alt={selectedPhoto.title}
                                className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
                            />

                            <div className="mt-3 rounded-lg bg-black/60 px-4 py-2 text-center text-sm text-white">
                                <span
                                    className={`mb-3 rounded-full px-3 py-1 text-xs font-semibold ${
                                        selectedPhoto.title === 'Meter Sebelum'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : selectedPhoto.title === 'Meter Sesudah'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    {selectedPhoto.title}
                                </span>
                                {selectedPhoto.datetime && (
                                    <div className="text-xs text-gray-300 mt-1">
                                        {selectedPhoto.datetime}
                                    </div>
                                )}

                                <div className="mt-1 text-xs text-gray-300">
                                    {selectedPhotoIndex + 1} / {photoGallery.length}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
                <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <User className="w-8 h-8 text-blue-600 mr-3" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Absensi Karyawan</h1>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                        {/* Search */}
                        <div className="relative">
                            <input type="text" value={searchName} onChange={(e) => { setSearchName(e.target.value); setCurrentPage(1); }} placeholder="Masukkan Nama atau No. Sambung" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            {/* <button onClick={() => {  }} className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                Cari Nama
                            </button> */}
                        </div>
                    </div>

                    <div className="flex space-x-3">
                        <button 
                            onClick={initDataSegel}
                            disabled={loading}
                            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                            <RotateCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            {loading ? "Memuat..." : "Muat Ulang"}
                        </button>
                    </div>
                    </div>
                </div>

                {/* Employee Table */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th onClick={() => handleSort('nsb')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Nomor Sambung {renderSortIcon('nsb')}
                            </th>
                            <th onClick={() => handleSort('nama')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Nama {renderSortIcon('nama')}
                            </th>
                            <th onClick={() => handleSort('alamat')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Alamat {renderSortIcon('alamat')}
                            </th>
                            <th onClick={() => handleSort('jumlah')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Jumlah {renderSortIcon('jumlah')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Aksi
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {
                            loading? 
                            <>
                                <tr>
                                    <td colSpan={6} className="text-center py-4 text-gray-500">
                                        Sedang Loading . . .
                                    </td>
                                </tr>
                            </>
                            :
                            <>
                                <RenderTableSegel data={paginatedSegels} onOpenDetail={handleDetailSegel} onRequestDelete={handleRequestDelete} onRemoveState={initDataSegel}/>
                                { showDeleteConfirm && <ConfirmModal title="Hapus Absensi" onConfirm={confirmDelete} onCancel={() => setShowDeleteConfirm(false)}>Apakah Anda yakin ingin menghapus absensi {deleteCandidate ? `${deleteCandidate.nsb} - ${deleteCandidate.nama}` : ''}?</ConfirmModal> }
                            </>
                            }
                        </tbody>
                    </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                    Menampilkan <span className="font-medium">{currentPage}</span> hingga{' '}
                    <span className="font-medium">{statusLength === "all" ? segelData.length : statusLength}</span> dari{' '}
                    <span className="font-medium">{segelData.length}</span> karyawan
                    </div>
                    <div className="flex space-x-2">
                    <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        Previous
                    </button>
                    
                    { pageNumbers.map((page) =>( 
                        <button 
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${ currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200" }`}>
                        {page}
                        </button>
                    ))}
                    
                    <button 
                        disabled={currentPage === Math.ceil(segelData.length / itemsPerPage)}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        Next
                    </button>
                    </div>
                </div>
                </div>

                {/* Add Employee Modal */}
                {/* {showFormModal && <FormAkunModal absensiDetail={selectedAbsensi} statusForm={statusFormModal} setShowFormModal={setShowFormModal} onPostState={initDataKaryawan}/>} */}
                {showDetailModal && <DetailModalSegel />}
            </div>
        </>
    )
}
